import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProgressBar, Table } from 'react-bootstrap';
import styles from './SchemaTemplatesCSVTool.module.css';
import ValidationFeedback from '../../components/ValidationFeedback/ValidationFeedback';
import {
  UpdateSchemaTemplatesBatch,
  CreateSchemaTemplatesBatch,
  ValidateSchemaTemplatesBatch,
  CreateScheduledFridgeSchemaChange,
} from '../../libs/GraphQL';
import {
  buildUpdateInputRow,
  roundUpToQuarterHour,
  formatScheduleDateTime,
  parseGoLiveDateTime,
  MODE_CARDS,
  COLUMNS_BY_MODE,
} from './SchemaTemplatesUtils';
import copyToClipboard from '../../utils/copyToClipboard';

const VALIDATION_TYPE = {
  ERRORS: 'errors',
  WARNINGS: 'warnings',
  SUCCESS: 'success',
};

const BATCH_SIZE = 500;

const REQUIRED_HEADERS = [
  'schemaId',
  'schemaType',
  'discount',
  'discountSchedule',
  'discountUI',
  'expiringDays',
  'priceDisplayType',
  'platform',
];

// Additional required headers for Create mode
const CREATE_MODE_REQUIRED_HEADERS = [
  ...REQUIRED_HEADERS,
  'replicaSchemaIdForBasePrices',
];

const SCHEDULE_MODE_REQUIRED_HEADERS = [...REQUIRED_HEADERS, 'goLiveDateTime'];

function parseCsv(text) {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((line) => line.split(',').some((c) => c.trim() !== ''));
  const header = (lines.shift() || '').split(',').map((h) => h.trim());
  const rows = lines.map((line) => {
    const cols = line.split(',').map((c) => c.trim());
    const obj = {};
    header.forEach((h, i) => (obj[h] = cols[i] || ''));
    return obj;
  });
  return { header, rows };
}

// Centralized builder for error CSV rows (frontend invalids + backend failures)
function buildErrorRows({ invalids = [], failures = [], parsedRows = [] }) {
  const rows = [];
  invalids.forEach((invalid) => {
    rows.push({
      lineNumber: invalid.lineNumber,
      ...invalid,
      error: Array.isArray(invalid.errors)
        ? invalid.errors.join('; ')
        : invalid.errors,
    });
  });
  failures.forEach((f) => {
    const match = parsedRows.find((row) => row.input?.schemaId === f.schemaId);
    const base = match
      ? { lineNumber: match.lineNumber, ...match.raw }
      : { schemaId: f.schemaId };
    rows.push({
      ...base,
      error: Array.isArray(f.errors) ? f.errors.join('; ') : f.errors,
    });
  });
  return rows;
}

const SchemaTemplatesCSVTool = () => {
  const BACKEND_ERROR_MAP = {
    PRICE_DISPLAY_INVALID:
      'priceDisplayType is required and must be EXPLICIT or IMPLICIT',
    TIME_WINDOWS_REQUIRED:
      'timeBound.timeWindows is required and must be a non-empty list',
  };

  // Mode state for switching between Update and Create operations
  const [mode, setMode] = useState('update');
  const [copyStatus, setCopyStatus] = useState('idle');

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setCopyStatus('idle');
    resetForm();
  };

  const handleCopyColumns = async () => {
    const header = COLUMNS_BY_MODE[mode].cols.map((c) => c.name).join('\t');
    const ok = await copyToClipboard(header);
    setCopyStatus(ok ? 'copied' : 'failed');
    setTimeout(() => setCopyStatus('idle'), 2000);
  };

  const displayCell = (value) => {
    const str = String(value ?? '').trim();
    return str === '' ? '–' : str;
  };
  function mapBackendMessage(errLike) {
    const code = errLike?.extensions?.code || errLike?.code;
    if (code && BACKEND_ERROR_MAP[code]) return BACKEND_ERROR_MAP[code];
    const message = typeof errLike === 'string' ? errLike : errLike?.message;
    if (!message) return '';
    const lower = message.toLowerCase();
    if (lower.includes('pricedisplaytype'))
      return BACKEND_ERROR_MAP.PRICE_DISPLAY_INVALID;
    if (lower.includes('timewindows'))
      return BACKEND_ERROR_MAP.TIME_WINDOWS_REQUIRED;
    if (lower.includes('coerced null value')) {
      return 'A required field is missing. Check required columns for this template type.';
    }
    if (
      lower.includes('timeboundscheduletemplateinput') &&
      lower.includes('not defined')
    ) {
      return 'Invalid field for timeBound. Allowed fields: discount, priceDisplay, timeWindows.';
    }
    if (
      lower.includes('updateschematemplatesinput') &&
      lower.includes('not defined')
    ) {
      return 'Invalid root field. Use nested expiring/timeBound/subsidy; do not send root-level discount.';
    }
    if (lower.includes('day_of_week')) {
      return 'Use timeWindows entries with camelCase keys: dayOfWeek, startTime, endTime.';
    }
    return message;
  }
  const [csvFile, setCsvFile] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedRows, setParsedRows] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [invalidRows, setInvalidRows] = useState([]);
  const [downloadErrors, setDownloadErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const csvFileRef = useRef(null);

  function downloadErrorCsv() {
    if (downloadErrors.length === 0) return;
    const columns = [
      'lineNumber',
      'schemaId',
      'schemaType',
      'discount',
      'sharedSubsidyClientDiscount',
      'discountSchedule',
      'discountUI',
      'expiringDays',
      'priceDisplayType',
      'platform',
      ...(mode === 'create' ? ['replicaSchemaIdForBasePrices'] : []),
      ...(mode === 'schedule' ? ['goLiveDateTime'] : []),
      'sleepScreenId',
      'error',
    ];
    // Escapes values for safe CSV export by handling special characters
    const escape = (value) => {
      // Convert null/undefined to empty string, everything else to string
      const stringValue =
        value === undefined || value === null ? '' : String(value);

      // Check if value contains CSV special characters (comma, quote, newline)
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        // CSV escaping: wrap in quotes and escape internal quotes by doubling them
        return '"' + stringValue.replace(/"/g, '""') + '"';
      }

      // Safe to return as-is if no special characters
      return stringValue;
    };
    const lines = [columns.join(',')].concat(
      downloadErrors.map((row) => columns.map((c) => escape(row[c])).join(',')),
    );
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schema-template-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const resetForm = (keepFeedback = false) => {
    setCsvFile(null);
    setParsedRows([]);
    setRowErrors({});
    setInvalidRows([]);
    if (!keepFeedback) setFeedback(null);
    setUploadProgress(0);
    if (csvFileRef.current) csvFileRef.current.value = null;
  };

  function validateAndParse(text) {
    setFeedback(null);
    const { header, rows } = parseCsv(text);

    // Use different required headers based on mode
    const requiredHeaders =
      mode === 'create'
        ? CREATE_MODE_REQUIRED_HEADERS
        : mode === 'schedule'
        ? SCHEDULE_MODE_REQUIRED_HEADERS
        : REQUIRED_HEADERS;
    const missing = requiredHeaders.filter((h) => !header.includes(h));
    if (missing.length) {
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: 'Header Validation',
            description: `Missing columns: ${missing.join(', ')}${
              mode === 'create'
                ? ' (Create mode requires replicaSchemaIdForBasePrices)'
                : ''
            }${
              mode === 'schedule'
                ? ' (Schedule mode requires goLiveDateTime)'
                : ''
            }`,
          },
        ],
      });
      setParsedRows([]);
      setRowErrors({});
      setInvalidRows([]);
      return;
    }

    const newRows = [];
    const errorsByIndex = {};
    const invalids = [];
    rows.forEach((r, idx) => {
      const { input, errors } = buildUpdateInputRow(r, mode);

      // Additional validation for Create mode: replicaSchemaIdForBasePrices is required
      if (mode === 'create') {
        const replicaSchemaId = String(
          r.replicaSchemaIdForBasePrices || '',
        ).trim();
        if (!replicaSchemaId) {
          errors.push(
            'replicaSchemaIdForBasePrices is required in Create mode',
          );
        }
      }

      let goLive;
      if (mode === 'schedule') {
        const { date, error } = parseGoLiveDateTime(r.goLiveDateTime);
        if (error) {
          errors.push(error);
        } else {
          const rounded = roundUpToQuarterHour(date);
          if (rounded.getTime() <= Date.now()) {
            errors.push('goLiveDateTime must be in the future');
          } else {
            goLive = rounded.toISOString();
          }
        }
      }

      newRows.push({ lineNumber: idx + 2, raw: r, input, goLive });
      if (errors.length) {
        errorsByIndex[idx] = errors;
        invalids.push({ lineNumber: idx + 2, ...r, errors });
      }
    });

    // Duplicate protection: block duplicate schemaId entries (esp. with mixed templateType)
    const bySchema = new Map();
    newRows.forEach((row, idx) => {
      const id = String(row.raw.schemaId || '').trim();
      if (!id) return;
      const type = String(
        row.input?.templateType || row.raw.schemaType || '',
      ).trim();
      // Same schema may repeat at different go-live times (apply now + revert
      // later), so schedule mode dedupes per (schemaId, time), not schemaId.
      const key = mode === 'schedule' ? `${id}@@${row.goLive || ''}` : id;
      if (!bySchema.has(key)) bySchema.set(key, []);
      bySchema.get(key).push({ idx, lineNumber: row.lineNumber, type });
    });
    bySchema.forEach((entries) => {
      if (entries.length <= 1) return;
      const typeSet = new Set(entries.map((e) => e.type).filter(Boolean));
      const sameTimeNote =
        mode === 'schedule' ? ' at the same go-live time' : '';
      const msg =
        typeSet.size > 1
          ? `Duplicate schemaId with mixed template types (${[...typeSet].join(
              ', ',
            )})`
          : `Duplicate schemaId found multiple times in CSV${sameTimeNote}`;
      entries.forEach(({ idx, lineNumber }) => {
        errorsByIndex[idx] = [...(errorsByIndex[idx] || []), msg];
        if (!invalids.find((invalid) => invalid.lineNumber === lineNumber)) {
          invalids.push({ lineNumber, ...newRows[idx].raw, errors: [msg] });
        }
      });
    });
    setParsedRows(newRows);
    setRowErrors(errorsByIndex);
    setInvalidRows(invalids);
    if (Object.keys(errorsByIndex).length > 0) {
      setDownloadErrors(
        buildErrorRows({ invalids, failures: [], parsedRows: newRows }),
      );
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: 'Fix CSV errors before upload',
            description: `${invalids.length} row(s) have errors.`,
          },
        ],
      });
      return;
    }
    setDownloadErrors([]);
    setUploadProgress(30);
  }

  function onFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadProgress(10);
      validateAndParse(ev.target.result);
      setCsvFile(file);
    };
    reader.readAsText(file);
  }

  async function submitSchedule(validRows) {
    // One job per distinct go-live time — the backend takes a single dateTime.
    const byTime = new Map();
    validRows.forEach((row) => {
      if (!byTime.has(row.goLive)) byTime.set(row.goLive, []);
      byTime.get(row.goLive).push(row.input);
    });

    setValidating(true);
    setFeedback(null);
    setUploadProgress(20);
    try {
      // Dry-run the whole batch first; block scheduling unless every row passes.
      const allInputs = validRows.map((row) => row.input);
      const validation = await ValidateSchemaTemplatesBatch(allInputs);
      const failedRows = (validation?.results || []).filter((r) => !r.success);
      if (validation?.success === false || failedRows.length > 0) {
        const failures = failedRows.map((r) => ({
          schemaId: r.schemaId,
          errors:
            r.errors && r.errors.length
              ? r.errors
              : [r.message || 'Validation failed'],
        }));
        setDownloadErrors(
          buildErrorRows({ invalids: [], failures, parsedRows }),
        );
        setFeedback({
          type: VALIDATION_TYPE.ERRORS,
          data: [
            {
              title: 'Validation failed — nothing was scheduled',
              description: `${
                failures.length || 'Some'
              } row(s) failed validation. Fix the CSV and re-upload.`,
              details:
                failures
                  .map(
                    (f) =>
                      `${f.schemaId}: ${f.errors
                        .map(mapBackendMessage)
                        .join('; ')}`,
                  )
                  .join('\n') || validation?.message,
            },
          ],
        });
        return;
      }

      setValidating(false);
      setLoading(true);
      setUploadProgress(30);
      const groups = [...byTime.entries()].sort((a, b) =>
        a[0].localeCompare(b[0]),
      );
      const created = [];
      const failedGroups = [];
      for (let i = 0; i < groups.length; i += 1) {
        const [dateTime, items] = groups[i];
        // Yield so the progress bar repaints between jobs.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 0));
        setUploadProgress(30 + Math.round(((i + 0.5) / groups.length) * 70));
        try {
          // eslint-disable-next-line no-await-in-loop
          const rows = await CreateScheduledFridgeSchemaChange({
            dateTime,
            items,
          });
          created.push({ rows, dateTime, count: items.length });
        } catch (groupErr) {
          failedGroups.push({
            dateTime,
            count: items.length,
            message:
              groupErr?.errors?.[0]?.message ||
              groupErr?.message ||
              String(groupErr),
          });
        }
        setUploadProgress(30 + Math.round(((i + 1) / groups.length) * 70));
      }

      const totalScheduled = created.reduce(
        (n, g) => n + (g.rows?.length ?? g.count),
        0,
      );
      const createdDetails = created
        .map(
          (g) =>
            `${formatScheduleDateTime(
              g.rows?.[0]?.actionTime || g.dateTime,
            )} — ${g.rows?.length ?? g.count} schema(s)${
              g.rows?.[0]?.jobId ? ` (job ${g.rows[0].jobId})` : ''
            }`,
        )
        .join('\n');

      if (failedGroups.length > 0) {
        setFeedback({
          type: VALIDATION_TYPE.ERRORS,
          data: [
            {
              title: 'Some scheduled jobs failed',
              description: `${failedGroups.length} of ${groups.length} job(s) could not be scheduled.`,
              details: failedGroups
                .map(
                  (g) =>
                    `${formatScheduleDateTime(g.dateTime)} (${
                      g.count
                    } schema(s)): ${mapBackendMessage(g.message)}`,
                )
                .join('\n'),
            },
            created.length > 0 && {
              title: 'Scheduled successfully',
              description: `${totalScheduled} change(s) across ${created.length} job(s).`,
              details: createdDetails,
            },
          ].filter(Boolean),
        });
        return;
      }

      setFeedback({
        type: VALIDATION_TYPE.SUCCESS,
        data: [
          {
            title:
              created.length > 1 ? 'Changes scheduled' : 'Change scheduled',
            description: `${totalScheduled} schema change(s) scheduled across ${created.length} job(s).`,
            details: createdDetails,
          },
        ],
      });
      resetForm(true);
    } catch (err) {
      const backendMsgs = Array.isArray(err?.errors)
        ? err.errors.map((e) => e?.message).filter(Boolean)
        : [];
      const description = backendMsgs.length
        ? backendMsgs.map(mapBackendMessage).join('\n')
        : mapBackendMessage(err?.message) || String(err);
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [{ title: 'Scheduling failed', description }],
      });
    } finally {
      setValidating(false);
      setLoading(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!csvFile) return;
    const validInputs = parsedRows
      .map((r, idx) => ({ idx, input: r.input }))
      .filter(({ idx }) => !(idx in rowErrors))
      .map(({ input }) => input);

    if (validInputs.length === 0 || invalidRows.length > 0) {
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: 'Fix CSV errors before upload',
            description:
              validInputs.length === 0
                ? 'No valid rows found.'
                : `${invalidRows.length} row(s) have errors. This upload is all-or-nothing; please correct them and re-upload.`,
          },
        ],
      });
      return;
    }

    if (mode === 'schedule') {
      const validRows = parsedRows.filter((_, idx) => !(idx in rowErrors));
      await submitSchedule(validRows);
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    try {
      const chunks = [];
      for (let i = 0; i < validInputs.length; i += BATCH_SIZE) {
        chunks.push(validInputs.slice(i, i + BATCH_SIZE));
      }

      const successes = [];
      const failures = [];

      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i];
        const offset = i * BATCH_SIZE;
        await new Promise((resolve) => setTimeout(resolve, 0));

        try {
          const batch =
            mode === 'create'
              ? await CreateSchemaTemplatesBatch(chunk)
              : await UpdateSchemaTemplatesBatch(chunk);

          if (batch?.results?.length) {
            batch.results.forEach((result, idx) => {
              if (result.success) {
                successes.push({
                  schemaId: result.schemaId,
                  message: result.message,
                });
              } else {
                failures.push({
                  schemaId: result.schemaId,
                  errors:
                    result.errors && result.errors.length
                      ? result.errors
                      : [result.message || 'Unknown error'],
                  index: offset + (result.index ?? idx),
                });
              }
            });
          } else if (batch?.success === false) {
            failures.push({
              schemaId: `BATCH_${i + 1}`,
              errors: [batch.message || 'Batch failed'],
            });
          }
        } catch (batchErr) {
          const backendMsgs = Array.isArray(batchErr?.errors)
            ? batchErr.errors.map((e) => e?.message).filter(Boolean)
            : [];
          const msg = backendMsgs.length
            ? backendMsgs.join('; ')
            : batchErr?.message || String(batchErr);
          failures.push({
            schemaId: `BATCH_${i + 1}`,
            errors: [
              /timed out|timeout/i.test(msg)
                ? `Batch ${i + 1} of ${
                    chunks.length
                  } timed out. Try a smaller file.`
                : /payload size exceeded/i.test(msg)
                ? `Batch ${i + 1} of ${
                    chunks.length
                  } payload too large. Try a smaller file.`
                : `Batch ${i + 1} of ${chunks.length} failed: ${msg}`,
            ],
          });
        }

        setUploadProgress(Math.round(((i + 1) / chunks.length) * 100));
      }

      setLoading(false);

      const hasErrors = failures.length > 0;
      const hasSuccesses = successes.length > 0;
      const hasFrontendInvalids = invalidRows.length > 0;

      if (hasErrors || hasFrontendInvalids) {
        // Prepare downloadable error rows
        const errorRows = buildErrorRows({
          invalids: invalidRows,
          failures,
          parsedRows,
        });
        setDownloadErrors(errorRows);
        const dataSections = [];
        if (hasFrontendInvalids) {
          dataSections.push({
            title: 'Frontend validation errors',
            description: 'Some rows were invalid before calling the API.',
            details: invalidRows
              .map(
                (invalidRow) =>
                  `${invalidRow.lineNumber}: ${
                    invalidRow.schemaId || invalidRow.raw?.schemaId || ''
                  } → ${invalidRow.errors.join('; ')}`,
              )
              .join('\n'),
          });
        }
        if (hasErrors) {
          dataSections.push({
            title: 'Backend validation errors',
            description: 'Some rows failed on the backend.',
            details: failures
              .map(
                (failure) =>
                  `${failure.schemaId}: ${
                    Array.isArray(failure.errors)
                      ? failure.errors.map(mapBackendMessage).join('; ')
                      : mapBackendMessage(failure.errors)
                  }`,
              )
              .join('\n'),
          });
          if (hasSuccesses) {
            dataSections.push({
              title: 'Successfully updated',
              description: `${successes.length} template(s) updated`,
              details: successes
                .map((s) => `${s.schemaId}: ${s.message || 'Updated'}`)
                .join('\n'),
            });
          }
        }
        // Already built above

        // Enforce all-or-nothing UX: if any error, do not proceed
        setFeedback({ type: VALIDATION_TYPE.ERRORS, data: dataSections });
        return;
      }

      setFeedback({
        type: VALIDATION_TYPE.SUCCESS,
        data: [
          hasFrontendInvalids
            ? {
                title: 'Frontend validation errors',
                description: `${invalidRows.length} row(s) skipped before upload`,
                details: invalidRows
                  .map(
                    (invalidRow) =>
                      `${invalidRow.lineNumber}: ${
                        invalidRow.schemaId || invalidRow.raw?.schemaId || ''
                      } → ${invalidRow.errors.join('; ')}`,
                  )
                  .join('\n'),
              }
            : null,
          {
            title:
              mode === 'create'
                ? 'Schema Templates Created'
                : 'Schema Templates Updated',
            description: `Processed ${validInputs.length} templates: ${
              successes.length
            } succeeded, ${failures.length} failed.${
              validInputs.length > BATCH_SIZE
                ? ` Processed in ${Math.ceil(
                    validInputs.length / BATCH_SIZE,
                  )} batches (${BATCH_SIZE} per batch).`
                : ''
            }`,
            details: hasSuccesses
              ? successes
                  .map(
                    (s) =>
                      `${s.schemaId}: ${
                        s.message || (mode === 'create' ? 'Created' : 'Updated')
                      }`,
                  )
                  .join('\n')
              : undefined,
          },
        ].filter(Boolean),
      });
      // Keep the success banner visible while clearing the form inputs
      resetForm(true);
    } catch (err) {
      setUploadProgress(100);
      setLoading(false);
      const backendMsgs = Array.isArray(err?.errors)
        ? err.errors.map((e) => e?.message).filter(Boolean)
        : Array.isArray(err?.response?.errors)
        ? err.response.errors.map((e) => e?.message).filter(Boolean)
        : [];
      const description = backendMsgs.length
        ? backendMsgs.map(mapBackendMessage).join('\n')
        : mapBackendMessage(err?.message) || JSON.stringify(err);
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: mode === 'create' ? 'Create failed' : 'Update failed',
            description,
          },
        ],
      });
    }
  }

  const invalidCount = invalidRows.length;
  const validCount = parsedRows.length - invalidCount;

  return (
    <main className={styles.container}>
      {feedback ? (
        <div aria-live="polite" role="status">
          <ValidationFeedback data={feedback.data} type={feedback.type} />
        </div>
      ) : null}
      <h1>Manage Fridge Schema Conditions</h1>
      <nav className={styles.crossLinks} aria-label="Related pages">
        <Link to="/schema-conditions" className={styles.crossLink}>
          View Schema Conditions →
        </Link>
        <Link to="/base-price-management" className={styles.crossLink}>
          Update Base Prices →
        </Link>
      </nav>

      <div
        className={styles.modeCards}
        role="radiogroup"
        aria-label="Operation mode"
      >
        {MODE_CARDS.map((m) => (
          <button
            key={m.value}
            type="button"
            className={styles.modeCard}
            data-mode={m.value}
            data-active={mode === m.value}
            aria-pressed={mode === m.value}
            onClick={() => handleModeChange(m.value)}
            disabled={loading}
          >
            <span className={styles.modeCardTitle}>
              <span className={styles.modeCardRadio} aria-hidden="true" />
              {m.title}
            </span>
            <span className={styles.modeCardBlurb}>{m.blurb}</span>
          </button>
        ))}
      </div>

      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.formWrapper}>
          <div className={styles.flexColumn}>
            <div className={styles.columnsPanel} data-mode={mode}>
              <div className={styles.columnsHeader}>
                <span className={styles.columnsLabel}>
                  {COLUMNS_BY_MODE[mode].label}
                </span>
                <button
                  type="button"
                  className={styles.copyBtn}
                  data-copied={copyStatus === 'copied'}
                  data-failed={copyStatus === 'failed'}
                  onClick={handleCopyColumns}
                >
                  {copyStatus === 'copied' && 'Copied!'}
                  {copyStatus === 'failed' && 'Copy failed — select manually'}
                  {copyStatus === 'idle' && 'Copy column names'}
                </button>
              </div>
              <div className={styles.columnsList}>
                {COLUMNS_BY_MODE[mode].cols.map((col) => (
                  <span
                    key={col.name}
                    className={`${styles.colChip} ${
                      col.type === 'required' ? styles.colChipRequired : ''
                    } ${col.type === 'new' ? styles.colChipNew : ''} ${
                      col.type === 'optional' ? styles.colChipOptional : ''
                    }`}
                  >
                    {col.type === 'optional'
                      ? `${col.name} (optional)`
                      : col.name}
                  </span>
                ))}
              </div>
              <p className={styles.helperLine}>
                {mode === 'update' && (
                  <>
                    Optional <code>sleepScreenId</code> sets a per-fridge
                    default sleep screen (catalog id from the Sleep Screen
                    Library). Leave blank to keep the current setting.
                  </>
                )}
                {mode === 'create' && (
                  <>
                    <strong>replicaSchemaIdForBasePrices</strong> — the existing
                    schema whose item prices become the base for the new one.
                    Blank <code>sleepScreenId</code> uses the standard default
                    video.
                  </>
                )}
                {mode === 'schedule' && (
                  <>
                    <strong>goLiveDateTime</strong> — date + time in{' '}
                    <strong>Central</strong> (e.g. <code>2026-07-01 14:30</code>{' '}
                    or <code>6/30/2026 15:00</code>). Rows sharing a time are
                    grouped into one scheduled job.
                  </>
                )}
              </p>
            </div>

            {mode === 'schedule' && (
              <div className={styles.scheduleBanner}>
                <span className={styles.scheduleIcon} aria-hidden="true" />
                <span>
                  After you submit, scheduled changes show up on{' '}
                  <Link
                    to="/schema-conditions"
                    className={styles.scheduleBannerLink}
                  >
                    View Schema Conditions
                  </Link>{' '}
                  under the <strong>Scheduled</strong> filter. Click the clock
                  next to a fridge to review or cancel before it fires.
                </span>
              </div>
            )}
            <div className={styles.fileRow}>
              <input
                id="csvUpload"
                type="file"
                accept=".csv"
                onChange={onFileChange}
                ref={csvFileRef}
                disabled={loading}
                className={styles.fileInput}
                data-mode={mode}
              />
              {invalidRows.length > 0 ? (
                <button
                  className={styles.button}
                  data-mode={mode}
                  onClick={downloadErrorCsv}
                  type="button"
                >
                  Download Invalid Rows (
                  {downloadErrors.length || invalidRows.length || 0})
                </button>
              ) : (
                <button
                  className={styles.button}
                  data-mode={mode}
                  type="submit"
                  disabled={
                    !csvFile || loading || validating || invalidRows.length > 0
                  }
                  title={
                    invalidRows.length > 0
                      ? 'Fix CSV errors before upload (all-or-nothing)'
                      : ''
                  }
                >
                  {validating
                    ? 'Validating…'
                    : loading
                    ? mode === 'schedule'
                      ? 'Scheduling…'
                      : 'Loading...'
                    : mode === 'create'
                    ? 'Create Templates'
                    : mode === 'schedule'
                    ? 'Schedule Change'
                    : 'Update Templates'}
                </button>
              )}
            </div>
            {(loading || validating) && (
              <div className={styles.progressBarContainer} data-mode={mode}>
                <ProgressBar
                  now={uploadProgress}
                  label={`${uploadProgress}%`}
                  animated
                  striped
                />
              </div>
            )}
          </div>
        </div>

        {parsedRows.length > 0 && (
          <div className={styles.tableContainer} data-mode={mode}>
            <h3>CSV Data Preview</h3>
            <div className={styles.summary}>
              <p>
                <strong>Summary:</strong> {parsedRows.length} total rows,{' '}
                <span
                  className={
                    validCount > 0
                      ? styles.summaryCountValid
                      : styles.summaryCountNeutral
                  }
                >
                  {validCount} valid
                </span>
                ,{' '}
                <span
                  className={
                    invalidCount > 0
                      ? styles.summaryCountInvalid
                      : styles.summaryCountNeutral
                  }
                >
                  {invalidCount} invalid
                </span>
              </p>
            </div>
            <div className={styles.tableWrapper}>
              <Table key={`table-${mode}`}>
                <thead>
                  <tr>
                    <th>Line #</th>
                    <th>schemaId</th>
                    <th>schemaType</th>
                    <th>discount</th>
                    <th>sharedSubsidyClientDiscount</th>
                    <th>discountSchedule</th>
                    <th>discountUI</th>
                    <th>expiringDays</th>
                    <th>priceDisplayType</th>
                    <th>platform</th>
                    {mode === 'create' && <th>replicaSchemaIdForBasePrices</th>}
                    {mode === 'schedule' && <th>goLiveDateTime</th>}
                    <th>sleepScreenId</th>
                    <th>Validation</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((row, idx) => {
                    const errors = rowErrors[idx] || [];
                    const isInvalid = errors.length > 0;
                    return (
                      <tr
                        key={idx}
                        className={
                          isInvalid ? styles.errorRow : styles.validRow
                        }
                      >
                        <td>{row.lineNumber}</td>
                        <td>{displayCell(row.raw.schemaId)}</td>
                        <td>{displayCell(row.raw.schemaType)}</td>
                        <td>{displayCell(row.raw.discount)}</td>
                        <td>
                          {displayCell(row.raw.sharedSubsidyClientDiscount)}
                        </td>
                        <td>{displayCell(row.raw.discountSchedule)}</td>
                        <td>{displayCell(row.raw.discountUI)}</td>
                        <td>{displayCell(row.raw.expiringDays)}</td>
                        <td>{displayCell(row.raw.priceDisplayType)}</td>
                        <td>{displayCell(row.raw.platform)}</td>
                        {mode === 'create' && (
                          <td>
                            {displayCell(row.raw.replicaSchemaIdForBasePrices)}
                          </td>
                        )}
                        {mode === 'schedule' && (
                          <td>
                            {row.goLive
                              ? formatScheduleDateTime(row.goLive)
                              : displayCell(row.raw.goLiveDateTime)}
                          </td>
                        )}
                        <td>{displayCell(row.raw.sleepScreenId)}</td>
                        <td>
                          {errors.length > 0 ? (
                            <ul className={styles.errorList}>
                              {errors.map((e, i) => (
                                <li key={i} className={styles.errorItem}>
                                  {e}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className={styles.validText}> Valid</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </form>
    </main>
  );
};

export default SchemaTemplatesCSVTool;
